import React, {memo, useRef, useEffect} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactECharts  from 'echarts-for-react';
import { formatNumber, getShortenMark, shortenNumberAuto, shortenNumberAutoK } from '../../helpers/utils';

export default memo(function StockPriceChart(props) {
    const refChart = useRef(null);
    //
    const { code, floorPrice, refPrice, ceilPrice } = props;
    
    const {t} = useTranslation();
    const language = useSelector(state => state.gShared.language);
    const c_counter = useSelector(state => state.gSymbol.symbolPriceChart[code]?.counter);
    const c_chartDatas = useSelector(state => state.gSymbol.symbolPriceChart[code]?.datas);

    //
    const getOptions = () => {
        let datas = [];

        let countCeilPrice = 0;
        let countIncrPrice = 0;
        let countRefPrice = 0;
        let countDecrPrice = 0;
        let countFloorPrice = 0;

        
        let minCeil = -1, maxCeil = -1;
        let minIncr = -1, maxIncr = -1;
        let minRef = -1, maxRef = -1;
        let minDecr = -1, maxDecr = -1;
        let minFloor = -1, maxFloor = -1;

        //
        if(c_chartDatas && c_chartDatas.length > 0) {
            const sortedChartDatas = c_chartDatas.sort((a, b) => a.price < b.price ? -1 : 1);
            for (let index = 0; index < sortedChartDatas.length; index++) {
                datas.push([sortedChartDatas[index].quantity, sortedChartDatas[index].price]);

                if(sortedChartDatas[index].price >= ceilPrice) {
                    countCeilPrice += 1;
                }
                else if(sortedChartDatas[index].price <= floorPrice) {
                    countFloorPrice += 1;
                }
                else if(sortedChartDatas[index].price > refPrice) {
                    countIncrPrice += 1;
                }
                else if(sortedChartDatas[index].price < refPrice) {
                    countDecrPrice += 1;
                }
                else if(sortedChartDatas[index].price === refPrice) {
                    countRefPrice += 1;
                }
            }
        }

        //
        if(countFloorPrice > 0) {
            minFloor = 0;
            maxFloor = countFloorPrice - 1;
        }

        if(countDecrPrice > 0)
        {
            minDecr = countFloorPrice;
            maxDecr = countFloorPrice + countDecrPrice - 1;
        }

        if(countRefPrice > 0)
        {
            minRef = countFloorPrice + countDecrPrice;
            maxRef = countFloorPrice + countDecrPrice + countRefPrice - 1;
        }

        if(countIncrPrice > 0)
        {
            minIncr = countFloorPrice + countDecrPrice + countRefPrice;
            maxIncr = countFloorPrice + countDecrPrice + countRefPrice + countIncrPrice - 1;
        }

        if(countCeilPrice > 0)
        {
            minCeil = countFloorPrice + countDecrPrice + countRefPrice + countIncrPrice;
            maxCeil = countFloorPrice + countDecrPrice + countRefPrice + countIncrPrice + countCeilPrice- 1;
        }

        //
        return {
            useUTC: false,
            textStyle: {
                fontFamily: "tahoma, 'Be Vietnam Pro'",
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                  type: "shadow",
                },
                textStyle: {
                    // lineHeight: 30,
                    color: '#fff',
                    fontSize: 12,
                    lineHeight: '21px'
                }, 
                padding: [5, 9],
                backgroundColor: 'rgba(47, 62, 76, 0.9)', 
                borderColor: 'transparent',
                confine: true,
                formatter: function (params) {
                    // console.log(params);
                    let rez = '<p>' + t('mucGia') + ': ' + shortenNumberAutoK(params[0].data[1] / 1000, null, {showMark: false}) + '</p>';
                    params.forEach(item => {
                        var xx = '<p class="chart-tooltip-row">' + item.marker + '<span>' + item.seriesName + ':</span>' + '<b class="text-right">' + formatNumber(item.data[0]) + '</b></p>'
                        rez += xx;
                    });
                    return rez;
                },
            },
            // visualMap: {
            //     show: false,
            //     seriesIndex: 0,
            //     pieces: [
            //         { min: minFloor, max: maxFloor, color: '#00CCCC' },
            //         { min: minDecr, max: maxDecr, color: '#DB3624' },
            //         { min: minRef, max: maxRef, color: '#FFD500' },
            //         { min: minIncr, max: maxIncr, color: '#00E500' },
            //         { min: minCeil, max: maxCeil, color: '#FF00FF' },
            //     ], 
            //     outOfRange: {
            //         color: '#42556C'
            //     }
            // },
            xAxis: {
                type: 'value',
                axisLabel : {
                    hideOverlap: true,
                    formatter: function (value) {
                        return shortenNumberAutoK(value, null, {showMark: false, maximumFractionDigits: 1}) + getShortenMark(value, language);
                    }
                },
                axisLine: {
                    onZero: false,
                },
                splitLine: {
                    lineStyle: {
                        color: '#666',
                    },
                },
            },
            yAxis: [
                {
                    name: t('mucGia'),
                    type: 'category',
                    position: 'bottom',
                    axisLabel : {
                        formatter: function (value) {
                            return (value / 1000).toFixed(2);
                        }
                    },
                    splitLine: {
                        lineStyle: {
                            color: '#666',
                        },
                    },
                }
            ],
            series: [
                {
                    yAxisIndex: 0,
                    name: t('tongKL'),
                    data: datas,
                    type: 'bar',
                    itemStyle: {
                        color: '#1c4b91',
                    },
                    barMaxWidth: 30,
                },
            ],
            grid: {
                left: "10px",
                right: "10px",
                top: "10px",
                bottom: "10px",
                containLabel: true,
            },
        }
    }

    useEffect(() => {
        if(refChart && refChart.current)
        {
            refChart.current.getEchartsInstance().setOption(getOptions());
        }
    });

    //
    return (
        <>
        {
            c_chartDatas && c_chartDatas.length > 0 ? (
                <ReactECharts style={{width: '100%', height: '100%', ...props.style}}
                    ref={refChart}
                    lazyUpdate={true}
                    option={getOptions()}
                />
            ) : <></>
        }
        </>
    )
}, (prevProps, nextProps) => {
    if (prevProps.code === nextProps.code && prevProps.floorPrice === nextProps.floorPrice
        && prevProps.refPrice === nextProps.refPrice && prevProps.ceilPrice === nextProps.ceilPrice) {
        return true; // props are equal
    }
    return false; // props are not equal -> update the component
})